import "./index.scss";
import React, { useEffect, useMemo, useRef } from "react";

import ViewPlayersTable from "../viewPlayersTable";

// The categories used to be seven hard-coded entries, so a block the admin
// named anything else - "Finisher", say - had no tab and could not be reached.
// The list is built from whatever the blocks actually contain instead, with
// the familiar ones kept in their usual order and anything new sorted in after
// them.
const PREFERRED = [
  "elite",
  "marquee",
  "Batter",
  "All Rounder",
  "Bowler",
  "Wicketkeeper",
  "uncapped",
];

const rank = (key) => {
  const i = PREFERRED.findIndex((p) => p.toLowerCase() === key.toLowerCase());
  return i === -1 ? PREFERRED.length : i;
};

const LABEL = { elite: "Elite", marquee: "Marquee", uncapped: "Uncapped" };

const label = (key) => LABEL[key] || key.trim();

const countIn = (block) =>
  (block?.startedPlayers?.length || 0) +
  (block?.players?.length || 0) +
  (block?.soldPlayers?.length || 0) +
  (block?.unsoldPlayers?.length || 0);

// `loading` is only passed by the public pages, which fetch the pool over the
// wire after the page is already up. Without it the list says the auction has
// no players for as long as the request takes.
const ViewPlayers = ({ keyState, blocks, loading }) => {
  const { key, setKey } = keyState;
  const activeTab = useRef(null);

  const keys = useMemo(() => {
    if (!blocks || Array.isArray(blocks)) return [];
    return Object.keys(blocks)
      .filter((k) => blocks[k]?.[0])
      .sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [blocks]);

  // The default selection is "elite", which not every auction has. Fall back to
  // whatever the first real category is rather than showing an empty panel.
  useEffect(() => {
    if (keys.length && !keys.includes(key)) setKey(keys[0]);
  }, [keys, key, setKey]);

  const active = keys.includes(key) ? key : keys[0];

  // On a phone the categories are a strip that scrolls sideways, so the
  // selected one can sit past the right edge on arrival - the first category
  // an auction has is often not the one that is live. "nearest" keeps this
  // from scrolling the page itself when the chip is already in view.
  useEffect(() => {
    activeTab.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active]);

  if (!keys.length) {
    return (
      <p className="au-empty">
        {loading ? "Loading the player list…" : "No player categories yet."}
      </p>
    );
  }

  return (
    <div className="au-players">
      <nav className="au-cats">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            ref={k === active ? activeTab : null}
            className={`au-cat ${k === active ? "active" : ""}`}
            onClick={() => setKey(k)}
          >
            <span className="au-cat-name">{label(k)}</span>
            <span className="au-cat-count">{countIn(blocks[k][0])}</span>
          </button>
        ))}
      </nav>

      <div className="au-players-body">
        <ViewPlayersTable players={blocks[active][0]} />
      </div>
    </div>
  );
};

export default ViewPlayers;
